import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// ---------------------------------------------------------------------------
// Web Push API client — mirrors backend/src/routes/push.ts (/api/push).
//
//   GET  /config        — public VAPID key, whether push is configured, devices
//   GET  /preferences   — the three category switches
//   PUT  /preferences   — change them
//   POST /subscribe     — register this browser
//   POST /unsubscribe   — drop this browser
//   POST /test          — send myself one
// ---------------------------------------------------------------------------

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function jsonHeaders(): Record<string, string> {
  return { 'Content-Type': 'application/json', ...authHeaders() };
}

/** One browser where notifications are on. The endpoint is never returned. */
export interface PushDevice {
  id: string;
  userAgent: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface PushConfig {
  /** False when the server has no VAPID keys — the UI hides the whole panel. */
  enabled: boolean;
  publicKey: string | null;
  devices: PushDevice[];
}

export interface PushPreferences {
  tasks: boolean;
  chat: boolean;
  voice: boolean;
  meetings: boolean;
  /** System alerts (a dependency failed). Only admins are ever sent these. */
  alerts: boolean;
  /** A leave request needs my decision, or mine was answered. */
  leaves: boolean;
}

export async function getPushConfig(): Promise<PushConfig> {
  const res = await apiFetch<Envelope<PushConfig>>('/api/push/config', {
    headers: authHeaders(),
  });
  return res.data;
}

export async function getPushPreferences(): Promise<PushPreferences> {
  const res = await apiFetch<Envelope<{ preferences: PushPreferences }>>('/api/push/preferences', {
    headers: authHeaders(),
  });
  return res.data.preferences;
}

export async function updatePushPreferences(
  input: Partial<PushPreferences>,
): Promise<PushPreferences> {
  const res = await apiFetch<Envelope<{ preferences: PushPreferences }>>('/api/push/preferences', {
    method: 'PUT',
    headers: jsonHeaders(),
    body: JSON.stringify(input),
  });
  return res.data.preferences;
}

export async function registerPushSubscription(subscription: PushSubscriptionJSON): Promise<void> {
  await apiFetch<Envelope<unknown>>('/api/push/subscribe', {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(subscription),
  });
}

export async function removePushSubscription(endpoint: string): Promise<void> {
  await apiFetch<Envelope<unknown>>('/api/push/unsubscribe', {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ endpoint }),
  });
}

export async function sendTestPush(): Promise<void> {
  await apiFetch<Envelope<unknown>>('/api/push/test', {
    method: 'POST',
    headers: jsonHeaders(),
  });
}
